import React, { useEffect, useState } from 'react';
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Divider,
  FormControl,
  FormControlLabel,
  FormHelperText,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
  alpha,
  useTheme,
} from '@mui/material';
import { ArrowBack as BackIcon, InfoOutlined as InfoIcon } from '@mui/icons-material';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useVenue } from '../../../../context/VenueContext';
import inventoryItemService, { extractInventoryError } from '../../../../services/inventoryItemService';
import inventoryMovementService from '../../../../services/inventoryMovementService';
import inventoryBalanceService from '../../../../services/inventoryBalanceService';
import inventoryLotService from '../../../../services/inventoryLotService';
import venueLocationService, { pickDefaultLocation } from '../../../../services/venueLocationService';
import InventoryQuantityBadge from '../../../../components/venue/InventoryQuantityBadge';
import type { InventoryBalance, InventoryLot, ItemLotLocation, InventoryItem, VenueLocation } from '../../../../types/venue';

type LotOption = InventoryLot & { locations: ItemLotLocation[] };

type MovementTypeOption = 'MANUAL_ADJUSTMENT' | 'WASTE';

const TYPE_LABELS: Record<MovementTypeOption, string> = {
  MANUAL_ADJUSTMENT: 'Adjustment',
  WASTE: 'Waste / Spoilage',
};

const ManualAdjustmentForm: React.FC = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { venueId } = useVenue();

  const preselectedItemId = searchParams.get('itemId') ?? '';
  const backPath = preselectedItemId
    ? `/admin/venues/${venueId}/inventory/${preselectedItemId}`
    : `/admin/venues/${venueId}/inventory`;

  // ── Context item + current balance ────────────────────────────────────────────
  const [contextItem, setContextItem] = useState<InventoryItem | null>(null);
  const [currentBalance, setCurrentBalance] = useState<InventoryBalance | null>(null);
  const [contextLoading, setContextLoading] = useState(false);

  useEffect(() => {
    if (!venueId || !preselectedItemId) return;
    setContextLoading(true);
    inventoryItemService.getItem(venueId, preselectedItemId)
      .then(item => setContextItem(item))
      .catch(() => { /* non-blocking */ })
      .finally(() => setContextLoading(false));
  }, [venueId, preselectedItemId]);

  // Prefill unitCost from last known cost once location-scoped balance loads
  useEffect(() => {
    if (currentBalance?.lastCost != null) {
      setUnitCost(String(currentBalance.lastCost));
    }
  }, [currentBalance]);

  // ── Locations ─────────────────────────────────────────────────────────────────
  const [locations, setLocations] = useState<VenueLocation[]>([]);
  const [locationsLoading, setLocationsLoading] = useState(false);

  useEffect(() => {
    if (!venueId) return;
    setLocationsLoading(true);
    venueLocationService.getLocations(venueId)
      .then(locs => {
        const active = locs.filter(l => l.active);
        setLocations(active);
        const def = pickDefaultLocation(active);
        if (def) setLocationId(id => id || def._id);
      })
      .catch(() => setLocations([]))
      .finally(() => setLocationsLoading(false));
  }, [venueId]);

  // ── Form state ────────────────────────────────────────────────────────────────
  const [locationId, setLocationId] = useState('');
  const [movementType, setMovementType] = useState<MovementTypeOption>('MANUAL_ADJUSTMENT');
  const [direction, setDirection] = useState<'in' | 'out'>('in');
  const [quantityInput, setQuantityInput] = useState('');
  const [unitCost, setUnitCost] = useState('');
  const [notes, setNotes] = useState('');

  // ── Location-scoped balance (depends on locationId declared above) ─────────────
  const [balanceLoading, setBalanceLoading] = useState(false);

  useEffect(() => {
    if (!venueId || !preselectedItemId || !locationId) {
      setCurrentBalance(null);
      return;
    }
    setBalanceLoading(true);
    setCurrentBalance(null);
    inventoryBalanceService.getBalance(venueId, preselectedItemId, locationId)
      .then(setCurrentBalance)
      .catch(() => setCurrentBalance(null))
      .finally(() => setBalanceLoading(false));
  }, [venueId, preselectedItemId, locationId]);

  // ── Lot state (lot-tracked items only) ───────────────────────────────────────
  const [lotOptions, setLotOptions] = useState<LotOption[]>([]);
  const [lotsLoading, setLotsLoading] = useState(false);
  const [selectedLot, setSelectedLot] = useState<LotOption | null>(null);
  const [forceNullLot, setForceNullLot] = useState(false);

  useEffect(() => {
    if (!venueId || !preselectedItemId || !contextItem?.lotTracked) {
      setLotOptions([]);
      setSelectedLot(null);
      setForceNullLot(false);
      return;
    }
    setLotsLoading(true);
    inventoryLotService.getItemLots(venueId, preselectedItemId)
      .then(res => setLotOptions(res.lots.filter(l => l.active)))
      .catch(() => setLotOptions([]))
      .finally(() => setLotsLoading(false));
  }, [venueId, preselectedItemId, contextItem?.lotTracked]);

  // ── Submission ────────────────────────────────────────────────────────────────
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [touched, setTouched] = useState(false);
  const [negativeGuardOpen, setNegativeGuardOpen] = useState(false);

  // ── Derived values ────────────────────────────────────────────────────────────
  const quantityNum = quantityInput !== '' ? Number(quantityInput) : null;
  const quantityError = touched && (quantityNum === null || quantityNum <= 0 || !Number.isInteger(quantityNum));
  const locationError = touched && !locationId;
  const isOut = direction === 'out' || movementType === 'WASTE';
  // Outbound lot-tracked movements don't require lot selection — FIFO handles it.
  const lotSectionError = touched && (contextItem?.lotTracked ?? false) && !isOut && !selectedLot && !forceNullLot;
  const signedQuantity =
    quantityNum != null ? (isOut ? -Math.abs(quantityNum) : Math.abs(quantityNum)) : 0;

  // For lot-tracked items with a selected lot, show that lot's qty at the chosen location.
  // Otherwise fall back to the null-lot balance quantity.
  const currentQty = selectedLot && locationId
    ? (selectedLot.locations.find(l => l.locationId === locationId)?.quantity ?? 0)
    : ((currentBalance as any)?.quantity ?? (currentBalance as any)?.totalQuantity ?? 0);

  const predictedBalance =
    locationId && signedQuantity !== 0
      ? currentQty + signedQuantity
      : null;

  // ── Submit ────────────────────────────────────────────────────────────────────
  const doSubmit = async (forceNegativeOverride = false) => {
    setSubmitting(true);
    setError(null);
    try {
      await inventoryMovementService.postManualMovement(venueId, {
        inventoryItemId: preselectedItemId,
        locationId,
        type: movementType,
        quantity: signedQuantity,
        ...(selectedLot && { lotId: selectedLot._id }),
        ...(forceNullLot && { forceNullLotOverride: true }),
        ...(forceNegativeOverride && { forceNegativeOverride: true }),
        ...(unitCost !== '' && { unitCost: Number(unitCost) }),
        ...(notes.trim() && { notes: notes.trim() }),
      });
      navigate(backPath);
    } catch (err: any) {
      const code = err?.response?.data?.code ?? err?.response?.data?.error;
      if (code === 'NEGATIVE_LOT_BALANCE_BLOCKED') {
        setNegativeGuardOpen(true);
      } else {
        setError(extractInventoryError(err));
      }
    } finally {
      setSubmitting(false);
    }
  };

  const handleSubmit = async () => {
    setTouched(true);
    if (quantityNum === null || quantityNum <= 0 || !Number.isInteger(quantityNum)) return;
    if (!preselectedItemId) return;
    if (!locationId) return;
    if ((contextItem?.lotTracked ?? false) && !isOut && !selectedLot && !forceNullLot) return;
    await doSubmit();
  };

  const handleConfirmNegative = async () => {
    setNegativeGuardOpen(false);
    await doSubmit(true);
  };

  // ── Render ────────────────────────────────────────────────────────────────────
  return (
    <Box sx={{ p: 3 }}>
      <Paper
        sx={{
          borderRadius: 2,
          overflow: 'hidden',
          boxShadow: '0 4px 12px rgba(0,0,0,0.08)',
          maxWidth: 600,
        }}
      >
        {/* Header */}
        <Box
          sx={{
            px: 3,
            py: 2.5,
            display: 'flex',
            alignItems: 'center',
            gap: 2,
            borderBottom: `1px solid ${alpha(theme.palette.divider, 0.12)}`,
          }}
        >
          <Button
            startIcon={<BackIcon />}
            onClick={() => navigate(backPath)}
            sx={{ textTransform: 'none', color: theme.palette.text.secondary }}
          >
            {preselectedItemId ? 'Item Detail' : 'Inventory'}
          </Button>
          <Divider orientation="vertical" flexItem />
          <Typography variant="h6" fontWeight={600}>Manual Adjustment</Typography>
        </Box>

        <Box sx={{ p: 3, display: 'flex', flexDirection: 'column', gap: 3 }}>
          {error && <Alert severity="error" onClose={() => setError(null)}>{error}</Alert>}

          {!preselectedItemId && (
            <Alert severity="info">
              Navigate to an inventory item and use the Adjust button to post a movement.
            </Alert>
          )}

          {/* Context card */}
          {contextLoading && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <CircularProgress size={14} />
              <Typography variant="body2" color="text.secondary">Loading item…</Typography>
            </Box>
          )}

          {contextItem && (
            <Box
              sx={{
                p: 2,
                borderRadius: 1.5,
                backgroundColor: alpha(theme.palette.primary.main, 0.06),
                border: `1px solid ${alpha(theme.palette.primary.main, 0.15)}`,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                gap: 2,
                flexWrap: 'wrap',
              }}
            >
              <Box>
                <Typography variant="subtitle2" fontWeight={600}>
                  {contextItem.displayName}
                </Typography>
              </Box>
              {locationId && (
                <Box sx={{ textAlign: 'right' }}>
                  <Typography variant="caption" color="text.secondary">
                    {'Stock at '}
                    {locations.find(l => l._id === locationId)?.name ?? '—'}
                  </Typography>
                  <Box sx={{ mt: 0.25 }}>
                    {balanceLoading ? (
                      <CircularProgress size={14} />
                    ) : selectedLot ? (
                      <InventoryQuantityBadge
                        quantity={selectedLot.locations.find(l => l.locationId === locationId)?.quantity ?? 0}
                        parLevel={contextItem.parLevel}
                        reorderPoint={contextItem.reorderPoint}
                      />
                    ) : currentBalance != null ? (
                      <InventoryQuantityBadge
                        quantity={(currentBalance as any).quantity ?? (currentBalance as any).totalQuantity ?? 0}
                        parLevel={contextItem.parLevel}
                        reorderPoint={contextItem.reorderPoint}
                      />
                    ) : (
                      <Typography variant="caption" color="text.disabled">No stock record</Typography>
                    )}
                  </Box>
                </Box>
              )}
            </Box>
          )}

          {/* Movement type */}
          <Box>
            <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 1 }}>Movement Type</Typography>
            <FormControl fullWidth size="small">
              <InputLabel>Type</InputLabel>
              <Select
                value={movementType}
                label="Type"
                onChange={e => setMovementType(e.target.value as MovementTypeOption)}
                disabled={!preselectedItemId}
              >
                {(Object.keys(TYPE_LABELS) as MovementTypeOption[]).map(t => (
                  <MenuItem key={t} value={t}>{TYPE_LABELS[t]}</MenuItem>
                ))}
              </Select>
              <FormHelperText>
                {movementType === 'WASTE'
                  ? 'Waste is always stock-out (negative quantity).'
                  : 'Adjustment can add or remove stock.'}
              </FormHelperText>
            </FormControl>
          </Box>

          {/* Direction — only for MANUAL_ADJUSTMENT */}
          {movementType === 'MANUAL_ADJUSTMENT' && (
            <Box>
              <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 1 }}>Direction</Typography>
              <ToggleButtonGroup
                exclusive
                value={direction}
                onChange={(_, val) => { if (val) setDirection(val); }}
                size="small"
                disabled={!preselectedItemId}
              >
                <ToggleButton value="in" sx={{ textTransform: 'none', fontWeight: 600, px: 3 }}>
                  + Stock In
                </ToggleButton>
                <ToggleButton value="out" sx={{ textTransform: 'none', fontWeight: 600, px: 3 }}>
                  − Stock Out
                </ToggleButton>
              </ToggleButtonGroup>
            </Box>
          )}

          {/* Location */}
          <Box>
            <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 1 }}>Location</Typography>
            <FormControl fullWidth size="small" error={locationError}>
              <InputLabel>Location</InputLabel>
              <Select
                value={locationId}
                label="Location"
                onChange={e => setLocationId(e.target.value)}
                disabled={!preselectedItemId || locationsLoading}
                endAdornment={locationsLoading ? <CircularProgress size={16} sx={{ mr: 2 }} /> : null}
              >
                {locations.map(loc => (
                  <MenuItem key={loc._id} value={loc._id}>{loc.name}</MenuItem>
                ))}
              </Select>
              {locationError && <FormHelperText>Select a location.</FormHelperText>}
            </FormControl>
          </Box>

          {/* Lot selector — lot-tracked items only */}
          {contextItem?.lotTracked && (
            <Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 1 }}>
                <Typography variant="subtitle2" fontWeight={600}>Lot</Typography>
                <Tooltip
                  title={
                    <Box sx={{ p: 0.5, display: 'flex', flexDirection: 'column', gap: 0.5, maxWidth: 280, color: 'common.white' }}>
                      <Box component="span" sx={{ fontWeight: 700, fontSize: '0.7rem' }}>Auto FIFO (outbound)</Box>
                      <Box component="span" sx={{ fontSize: '0.7rem', lineHeight: 1.5 }}>Leave lot blank to let the system allocate from the oldest lots first. If quantity spans multiple lots, one movement is created per lot consumed.</Box>
                      <Box component="span" sx={{ fontWeight: 700, fontSize: '0.7rem', mt: 0.25 }}>Select a specific lot</Box>
                      <Box component="span" sx={{ fontSize: '0.7rem', lineHeight: 1.5 }}>Use this for recalls, targeted write-offs, or expiry handling where you need to touch a known batch.</Box>
                    </Box>
                  }
                  placement="right"
                  arrow
                >
                  <InfoIcon sx={{ fontSize: 15, color: 'text.disabled', cursor: 'default' }} />
                </Tooltip>
              </Box>
              {!forceNullLot && (
                <Autocomplete<LotOption>
                  size="small"
                  options={lotOptions.slice().sort((a, b) => {
                    const pri = (s: string) => (s === 'GOODS_RECEIPT' || s === 'PRODUCTION' ? 0 : 1);
                    return pri(a.sourceType) - pri(b.sourceType);
                  })}
                  loading={lotsLoading}
                  value={selectedLot}
                  onChange={(_, val) => setSelectedLot(val)}
                  getOptionLabel={opt => opt.lotNumber}
                  isOptionEqualToValue={(a, b) => a._id === b._id}
                  disabled={!preselectedItemId}
                  renderOption={(props, opt) => {
                    const locQty = locationId
                      ? (opt.locations.find(l => l.locationId === locationId)?.quantity ?? 0)
                      : null;
                    const typeLabel =
                      opt.sourceType === 'ADJUSTMENT' ? 'Adjustment lot' :
                      opt.sourceType === 'COUNT_GAIN'  ? 'Count gain lot' : null;
                    return (
                      <Box component="li" {...props}>
                        <Box>
                          <Typography variant="body2">{opt.lotNumber}</Typography>
                          <Typography variant="caption" color="text.secondary">
                            {opt.supplierBatchRef ? `Batch: ${opt.supplierBatchRef} · ` : ''}
                            {locQty !== null ? `${locQty} at location` : 'qty unknown'}
                            {typeLabel ? ` · ${typeLabel}` : ''}
                          </Typography>
                        </Box>
                      </Box>
                    );
                  }}
                  renderInput={params => (
                    <TextField
                      {...params}
                      label="Lot"
                      error={lotSectionError}
                      helperText={
                        lotSectionError
                          ? 'Select a lot or post to unassigned stock.'
                          : selectedLot && locationId
                          ? (() => {
                              const q = selectedLot.locations.find(l => l.locationId === locationId)?.quantity ?? 0;
                              return `${q} available at this location`;
                            })()
                          : isOut
                          ? 'Optional — leave blank to consume lots automatically (FIFO).'
                          : 'Select which lot this movement targets.'
                      }
                      InputProps={{
                        ...params.InputProps,
                        endAdornment: (
                          <>
                            {lotsLoading ? <CircularProgress size={14} /> : null}
                            {params.InputProps.endAdornment}
                          </>
                        ),
                      }}
                    />
                  )}
                />
              )}
              {/* "Post to unassigned stock" is only valid for inbound movements.
                  For outbound (WASTE / stock-out), leaving lot blank triggers Auto FIFO,
                  which correctly decrements named lot balances oldest-first. */}
              {!isOut && (
                <FormControlLabel
                  sx={{ mt: forceNullLot ? 0 : 0.5 }}
                  control={
                    <Checkbox
                      size="small"
                      checked={forceNullLot}
                      onChange={e => {
                        setForceNullLot(e.target.checked);
                        if (e.target.checked) setSelectedLot(null);
                      }}
                      disabled={!preselectedItemId}
                    />
                  }
                  label={<Typography variant="body2">Post to unassigned stock</Typography>}
                />
              )}
              {!isOut && forceNullLot && (
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.25 }}>
                  Movement will target the null-lot bucket — use for pre-lot stock corrections.
                </Typography>
              )}
              {isOut && !selectedLot && (
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.75 }}>
                  No lot selected — stock will be consumed from named lots automatically, oldest first (FIFO).
                </Typography>
              )}
            </Box>
          )}

          {/* Quantity */}
          <Box>
            <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 1 }}>Quantity (bottles)</Typography>
            <TextField
              fullWidth
              size="small"
              type="number"
              label="Quantity"
              value={quantityInput}
              onChange={e => setQuantityInput(e.target.value)}
              inputProps={{ min: 1, step: 1 }}
              disabled={!preselectedItemId}
              error={quantityError}
              helperText={
                quantityError
                  ? 'Enter a positive whole number.'
                  : signedQuantity !== 0
                  ? `Will post ${signedQuantity > 0 ? '+' : ''}${signedQuantity} to movement history.`
                  : 'Enter the number of bottles.'
              }
            />
          </Box>

          {/* Predicted balance preview */}
          {predictedBalance !== null && (
            <Box
              sx={{
                p: 2,
                borderRadius: 1.5,
                backgroundColor: alpha(
                  predictedBalance < 0 ? theme.palette.warning.main : theme.palette.success.main,
                  0.07,
                ),
                border: `1px solid ${alpha(
                  predictedBalance < 0 ? theme.palette.warning.main : theme.palette.success.main,
                  0.2,
                )}`,
                display: 'flex',
                alignItems: 'center',
                gap: 2,
              }}
            >
              <Box>
                <Typography variant="caption" color="text.secondary">Expected balance after posting</Typography>
                <Box sx={{ mt: 0.5 }}>
                  <InventoryQuantityBadge
                    quantity={predictedBalance}
                    parLevel={contextItem?.parLevel}
                    reorderPoint={contextItem?.reorderPoint}
                  />
                </Box>
              </Box>
              {predictedBalance < 0 && (
                <Typography variant="caption" color="warning.dark">
                  Negative balance is allowed — adjust your stock count if needed.
                </Typography>
              )}
            </Box>
          )}

          {/* Unit cost (optional) */}
          <TextField
            fullWidth
            size="small"
            type="number"
            label="Unit Cost (optional)"
            value={unitCost}
            onChange={e => setUnitCost(e.target.value)}
            inputProps={{ min: 0, step: 0.01 }}
            disabled={!preselectedItemId}
            helperText="Cost per bottle — updates last cost on balance."
          />

          {/* Notes */}
          <TextField
            fullWidth
            size="small"
            label="Notes / Reason"
            multiline
            rows={3}
            value={notes}
            onChange={e => setNotes(e.target.value)}
            inputProps={{ maxLength: 2000 }}
            disabled={!preselectedItemId}
            helperText="Visible in movement history."
          />

          {/* Ledger warning */}
          <Box
            sx={{
              p: 2,
              backgroundColor: alpha(theme.palette.warning.main, 0.08),
              borderRadius: 1,
              border: `1px solid ${alpha(theme.palette.warning.main, 0.2)}`,
            }}
          >
            <Typography variant="body2" color="warning.dark">
              This posts a permanent entry to <strong>movement history</strong>.
              It cannot be deleted — only corrected with a reverse adjustment.
            </Typography>
          </Box>

          {/* Actions */}
          <Box sx={{ display: 'flex', gap: 1.5, justifyContent: 'flex-end' }}>
            <Button onClick={() => navigate(backPath)} sx={{ textTransform: 'none' }}>
              Cancel
            </Button>
            <Button
              variant="contained"
              color="warning"
              onClick={handleSubmit}
              disabled={submitting || !preselectedItemId}
              startIcon={submitting ? <CircularProgress size={16} color="inherit" /> : null}
              sx={{ textTransform: 'none', fontWeight: 600 }}
            >
              {submitting ? 'Posting…' : 'Post Adjustment'}
            </Button>
          </Box>
        </Box>
      </Paper>

      {/* Negative lot balance confirmation dialog */}
      <Dialog open={negativeGuardOpen} onClose={() => setNegativeGuardOpen(false)}>
        <DialogTitle>Negative Lot Balance</DialogTitle>
        <DialogContent>
          <DialogContentText>
            This adjustment would make the selected lot&apos;s balance negative at this location.
            Do you want to proceed anyway?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setNegativeGuardOpen(false)} sx={{ textTransform: 'none' }}>
            Cancel
          </Button>
          <Button
            variant="contained"
            color="warning"
            onClick={handleConfirmNegative}
            sx={{ textTransform: 'none', fontWeight: 600 }}
          >
            Post Anyway
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ManualAdjustmentForm;
